import { registeredTableHandlers } from '@/global/components/view-layouts/tabs-layout/config/registered-table-handlers'
import { api } from '@/global/services/api'
import { showDetailsAction, deleteAction, activationAction } from '@/global/components/table/helpers/tableActions'

export default {
  computed: {
    tableRowActions () {
      return [
        {
          ...showDetailsAction(this.$t, (event) => this.showEdit(event)),
          visible: !!this.permissions?.canUpdate
        },
        {
          ...activationAction(this.$t, (event) => this.onUserActivation(event), (event) => !event?.data?.deactivated_at),
          visible: !!this.permissions?.canActivate
        },
        {
          label: this.$t('base.reset_password'),
          icon: 'mdi-lock-reset',
          click: (event) => this.resetPassword(event)
        },
        {
          label: this.$t('base.impersonate_title'),
          icon: 'mdi-login',
          visible: !!this.permissions?.canImpersonate,
          click: (event) => this.impersonateUser(event)
        },
        {
          ...deleteAction(this.$t, (event) => this.onUserDelete(event)),
          visible: !!this.permissions?.canDelete
        }
      ]
    }
  },

  methods: {
    showEdit (event) {
      const { data } = event || {}
      const {
        id,
        first_name: firstName,
        last_name: lastName
      } = data || {}

      // do not show user edit view if id is invalid
      if (!id) {
        return
      }

      // used as breadcrumb value
      const query = {
        user: (firstName || '') + ' ' + (lastName || '')
      }

      this.$router.push({
        name: 'User',
        params: {
          id
        },
        query
      }).then()
    },

    onUserActivation (event) {
      const {
        deactivated_at: deactivatedAt,
        id,
        first_name: firstName,
        last_name: lastName
      } = event?.data || {}

      if (!id) {
        return
      }

      const title = !deactivatedAt ? this.$t('base/user.deactivate') : this.$t('base/user.activate')
      const fullName = ((firstName || '') + ' ' + (lastName || '')).trim()
      this.$set(this.informationDialogConfig, 'title', title + ' - ' + (fullName || '-'))
      this.$set(this.informationDialogConfig, 'positiveBtnText', !deactivatedAt ? this.$t('base.deactivate') : this.$t('base.activate'))
      this.$set(this.informationDialogConfig, 'message', !deactivatedAt ? this.$t('base/user.confirm_deactivation') : this.$t('base/user.confirm_activation'))
      this.$set(this.informationDialogConfig, 'callbackFn', () => this.handleUserActivation(event))
      this.$set(this.informationDialogConfig, 'openDialog', true)
    },

    async handleUserActivation (event) {
      try {
        const { id, deactivated_at: deactivatedAt } = event?.data || {}
        const { fetchData } = registeredTableHandlers?.[this.mainTableKey] || {}
        await api('base').patch(`user/${id}/${deactivatedAt ? 'activate' : 'deactivate'}`)

        this.$set(this.informationDialogConfig, 'openDialog', false)
        if (typeof fetchData === 'function') {
          fetchData()
        }
      }
      catch (exception) {
        console.error(exception)
      }
    },

    resetPassword (event) {
      const {
        id,
        first_name: firstName,
        last_name: lastName
      } = event?.data || {}

      if (!id) {
        return
      }

      const title = this.$t('base.reset_password')
      const fullName = ((firstName || '') + ' ' + (lastName || '')).trim()
      this.$set(this.informationDialogConfig, 'title', title + ' - ' + (fullName || '-'))
      this.$set(this.informationDialogConfig, 'positiveBtnText', this.$t('base.reset_password'))
      this.$set(this.informationDialogConfig, 'message', this.$t('base.reset_password_confirm_message'))
      this.$set(this.informationDialogConfig, 'callbackFn', () => this.handlePasswordReset(event))
      this.$set(this.informationDialogConfig, 'openDialog', true)
    },

    async handlePasswordReset (event) {
      try {
        const { id } = event?.data || {}

        await api('base').post(`user/${id}/reset-password`)

        this.$set(this.informationDialogConfig, 'openDialog', false)
      }
      catch (exception) {
        console.error(exception)
      }
    },

    impersonateUser (event) {
      const {
        id,
        first_name: firstName,
        last_name: lastName
      } = event?.data || {}

      if (!id) {
        return
      }

      const title = this.$t('base.impersonate_title')
      const fullName = ((firstName || '') + ' ' + (lastName || '')).trim()
      this.$set(this.informationDialogConfig, 'title', title)
      this.$set(this.informationDialogConfig, 'positiveBtnText', this.$t('base.login'))
      this.$set(this.informationDialogConfig, 'message', this.$t('base.impersonate_message') + (fullName || ''))
      this.$set(this.informationDialogConfig, 'callbackFn', () => this.handleUserImpersonate(event))
      this.$set(this.informationDialogConfig, 'openDialog', true)
    },

    async handleUserImpersonate (event) {
      try {
        const { id } = event?.data || {}

        await api('base').post(`user/${id}/change-user`)

        // redirect user to the home page after changing user
        await this.$router.push('/').then(() => {
          this.$router.go(0)
        })
      }
      catch (exception) {
        console.error(exception)
      }
    },

    onUserDelete (event) {
      const {
        id,
        first_name: firstName,
        last_name: lastName
      } = event?.data || {}

      if (!id) {
        return
      }

      const title = this.$t('base/user.delete')
      const fullName = ((firstName || '') + ' ' + (lastName || '')).trim()
      this.$set(this.informationDialogConfig, 'title', title + ' - ' + (fullName || '-'))
      this.$set(this.informationDialogConfig, 'positiveBtnText', this.$t('base.save'))
      this.$set(this.informationDialogConfig, 'message', this.$t('base/user.confirm_delete'))
      this.$set(this.informationDialogConfig, 'callbackFn', () => this.handleUserDeletion(event))
      this.$set(this.informationDialogConfig, 'openDialog', true)
    },

    async handleUserDeletion (event) {
      try {
        const { id } = event?.data || {}
        const { fetchData } = registeredTableHandlers?.[this.mainTableKey] || {}

        await api('base').delete(`user/${id}`)

        this.$set(this.informationDialogConfig, 'openDialog', false)
        if (typeof fetchData === 'function') {
          fetchData()
        }
      }
      catch (exception) {
        console.error(exception)
      }
    }
  }
}
